import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_INFO, SG_SURV_INFO_LANG_LIST } from "./services/PtliSurvMgmtService";

/*
 * Survey Management Registration
 * !== 설문 관리 등록 ==!
 */
export const PTLI0418002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const langCodes = ["KO", "EN", "TZ"];

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survInfo: useForm({
            defaultSchema: SF_SURV_INFO,
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survInfoLangList: useGrid({
            defaultSchema: SG_SURV_INFO_LANG_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        createSurv: useFetch({
            api: (data) =>
                APIS.createSurv({ ...form.survInfo.getValues(), survInfoLangList: grid.survInfoLangList.getData() }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli0418001q);
                    },
                    onCancel: () => {
                        navigate(URLS.ptli0418001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.survInfo.setErrors(error.response.data.errors);
                }
                modal.openModal({
                    content: "msg.ptl.00836",
                });
            },
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        createSurv: () => {
            if (!comnUtils.isEmpty(form.survInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.survInfo.handleSubmit(
                () => {
                    grid.survInfoLangList.clearData();

                    langCodes.forEach((langCd) => {
                        const ttle = form.survInfo.getValue(`qustnrTtle${langCd}`);
                        const pups = form.survInfo.getValue(`qustnrPupsCn${langCd}`);

                        // 세 필드 중 하나라도 있으면 행 추가
                        if (!comnUtils.isEmpty(ttle) || !comnUtils.isEmpty(pups)) {
                            grid.survInfoLangList.addRow({
                                langCd, // "KO" | "EN" | "TZ"
                                qustnrTtle: ttle ?? "",
                                qustnrPupsCn: pups ?? "",
                            });
                        }
                    });

                    // (선택) 언어행 하나도 없으면 경고
                    if (grid.survInfoLangList.getData().length === 0) {
                        modal.openModal({ content: "msg.ptl.00683" /* 최소 1개 언어 입력 요청 */ });
                        return;
                    }

                    // 3) 기간 값 분해 (reqDateRnge가 [시작,종료] 형태라면)
                    const [st, ed] = form.survInfo.getValue("reqDateRnge") ?? [];
                    if (st) form.survInfo.setValue("qustnrStrtDt", st);
                    if (ed) form.survInfo.setValue("qustnrEndDt", ed);

                    // 4) 저장 확인 후 호출
                    const data = {
                        ...form.survInfo.getValues(),
                        survInfoLangList: grid.survInfoLangList.getData(),
                    };

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createSurv.fetch(data);
                        },
                        close: false,
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.bsopClsfCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.ptlTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.mnPgeQustnrYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleEN}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleTZ}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTtleKO}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnEN}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnTZ}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrPupsCnKO}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrMakeInfoCn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.qustnrTrgtAcrdtYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survInfo.schema.reqDateRnge}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Group.Section hidden={true}>
                        {" "}
                        <Grid {...grid.survInfoLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0418001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.createSurv();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
